import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Search } from "lucide-react";
import React, { useState } from "react";

import {
  searchNomenclature1C,
  searchSupplier1C,
  type OneCSearchResult,
} from "@/api/product-dev";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiErr } from "@/pages/product-dev/format";

interface Props {
  kind: "nomenclature" | "supplier";
  onPick: (r: OneCSearchResult) => void;
  trigger: React.ReactNode;
}

/** Поповер лайв-поиска в 1С (номенклатура/контрагент). Поиск по кнопке «Найти»,
 * чтобы не дёргать 1С на каждый символ. Ошибки подключения/OData показываем явно. */
export function OneCSearchPopover({ kind, onPick, trigger }: Props): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");

  const search = useQuery({
    queryKey: ["pdev", "1c-search", kind, term],
    queryFn: () =>
      kind === "nomenclature" ? searchNomenclature1C(term) : searchSupplier1C(term),
    enabled: open && term.trim().length >= 2,
    retry: false,
    staleTime: 60_000,
  });

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setQ("");
          setTerm("");
        }
      }}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start">
        <form
          className="mb-2 flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            setTerm(q.trim());
          }}
        >
          <Input
            autoFocus
            placeholder={kind === "nomenclature" ? "Номенклатура 1С…" : "Контрагент 1С…"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-8"
          />
          <Button type="submit" size="icon" variant="outline" className="size-8 shrink-0">
            <Search className="size-4" />
          </Button>
        </form>

        {search.isFetching && <div className="px-1 py-2 text-xs text-muted-foreground">Ищем в 1С…</div>}
        {search.isError && (
          <div className="flex items-start gap-1.5 px-1 py-2 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            {apiErr(search.error, "Ошибка поиска в 1С")}
          </div>
        )}

        <div className="max-h-64 space-y-0.5 overflow-y-auto">
          {(search.data ?? []).map((r) => (
            <button
              key={r.ref_key + (r.article ?? "")}
              type="button"
              className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
              onClick={() => {
                onPick(r);
                setOpen(false);
              }}
            >
              <div className="truncate font-medium">{r.name ?? "(без названия)"}</div>
              {(r.article || r.inn) && (
                <div className="text-xs text-muted-foreground">{r.article ?? r.inn}</div>
              )}
            </button>
          ))}
          {search.data?.length === 0 && (
            <div className="px-1 py-2 text-xs text-muted-foreground">Ничего не найдено</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
